import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { cn } from "../../../../lib/utils";

interface ITextCellProps {
  value?: string;
  isEditable?: boolean;
  onChange?: (value: string) => void;
  numberOfRows?: number;
  className?: string;
  textClassName?: string;   
  textareaClassName?: string;
  containerClassName?: string;
  style?: React.CSSProperties;
  enableTooltip?: boolean;
  truncateChars?: number;
  enableScroll?: boolean;
}

const TextCell: React.FC<ITextCellProps> = ({
  value = "",
  isEditable,
  onChange,
  numberOfRows = 2,
  className,
  textClassName,
  textareaClassName,
  containerClassName,
  style,
  enableTooltip= true,
  truncateChars,
  enableScroll = false,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties | null>(
    null
  );

  const truncateWithEllipsis = (text: string, maxChars: number) => {
    const t = text.trim();
    if (t.length <= maxChars) return t;
    return `${t.slice(0, Math.max(0, maxChars - 1))}…`;
  };

  const rawValue = value ?? "";
  const displayText = useMemo(() => {
    if (!truncateChars) return rawValue;
    return truncateWithEllipsis(rawValue, truncateChars);
  }, [rawValue, truncateChars]);

  useEffect(() => {
    if (!isFocused) return;
    queueMicrotask(() => textareaRef.current?.focus());
  }, [isFocused]);

  const updateTooltipPosition = () => {
    const anchor = containerRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    setTooltipStyle({
      position: "fixed",
      left: rect.left,
      top: rect.bottom + 4, // matches `mt-1`
      zIndex: 9999,
    });
  };

  useLayoutEffect(() => {
    if (!enableTooltip || !showTooltip) return;
    updateTooltipPosition();
    const onScrollOrResize = () => updateTooltipPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [showTooltip, value, isFocused, enableTooltip]);

  if (!isEditable) {
    return (
      <div
        ref={containerRef}
        className={cn("relative px-2 py-1 group flex items-center", className)}
        style={style}
        onMouseEnter={() => {
          if (!enableTooltip) return;
          setShowTooltip(true);
          queueMicrotask(() => updateTooltipPosition());
        }}
        onMouseLeave={() => enableTooltip && setShowTooltip(false)}
      >
        <div
          ref={textRef}
          className={cn("font-sans font-normal text-sm", textClassName)}
          style={{
            display: "-webkit-box",
            WebkitLineClamp: numberOfRows ?? 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: "1.25rem",
            color: "#0A0A0A",
            letterSpacing: "0%",
          }}
        >
          {value || "-"}
        </div>
        {enableTooltip &&
          showTooltip &&
          value &&
          value !== "-" &&
          tooltipStyle &&
          ReactDOM.createPortal(
            <div
              className="absolute z-50 left-0 top-full mt-1 bg-gray-700 rounded-md shadow-lg whitespace-pre-wrap break-words font-sans font-normal text-xs leading-[1rem]"
              style={{
                ...tooltipStyle,
                color: "#FAFAFA",
                width: "146px",
                maxWidth: "384px",
                paddingTop: "0.375rem",
                paddingRight: "0.75rem",
                paddingBottom: "0.375rem",
                paddingLeft: "0.75rem",
                gap: "8px",
                opacity: 1,
              }}
            >
              {value}
              <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-700 transform rotate-45" />
            </div>,
            document.body,
          )}
      </div>
    );
  }

  if (isEditable && truncateChars && !isFocused) {
    const hasValue = rawValue.trim().length > 0;
    const shown = hasValue ? displayText : "";
    const clampLines = Math.max(1, numberOfRows ?? 1);

    return (
      <div
        ref={containerRef}
        className={cn("relative px-2 py-1 group flex items-center", className)}
        style={style}
        role="textbox"
        tabIndex={0}
        aria-label="Editable cell"
        onMouseEnter={() => {
          if (!enableTooltip) return;
          setShowTooltip(true);
          queueMicrotask(() => updateTooltipPosition());
        }}
        onMouseLeave={() => enableTooltip && setShowTooltip(false)}
        onMouseDown={(e) => {
          e.preventDefault();
          setIsFocused(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsFocused(true);
          }
        }}
        title={hasValue ? rawValue : undefined}
      >
        <div
          className={cn(
            "font-sans font-normal text-sm w-full",
            hasValue ? "text-[#0A0A0A]" : "text-[#0A0A0A]/60",
            textClassName
          )}
          style={{
            display: "-webkit-box",
            WebkitLineClamp: clampLines,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: "1.25rem",
            color: hasValue ? "#0A0A0A" : "rgba(10,10,10,0.6)",
            letterSpacing: "0%",
            whiteSpace: "normal",
          }}
        >
          {hasValue ? shown : "Add"}
        </div>

        {enableTooltip &&
          showTooltip &&
          hasValue &&
          tooltipStyle &&
          ReactDOM.createPortal(
            <div
              className="absolute z-50 left-0 top-full mt-1 bg-gray-700 rounded-md shadow-lg whitespace-pre-wrap break-words font-sans font-normal text-xs leading-[1rem]"
              style={{
                ...tooltipStyle,
                color: "#FAFAFA",
                width: "146px",
                maxWidth: "384px",
                paddingTop: "0.375rem",
                paddingRight: "0.75rem",
                paddingBottom: "0.375rem",
                paddingLeft: "0.75rem",
                gap: "8px",
                opacity: 1,
              }}
            >
              {rawValue}
              <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-700 transform rotate-45" />
            </div>,
            document.body
          )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        "w-full relative",
        "focus:bg-[#F3F4F6] focus:border-[#4B5563] focus:outline-none",
        containerClassName
      )}
      onMouseEnter={() => {
        if (!enableTooltip) return;
        setShowTooltip(true);
        queueMicrotask(() => updateTooltipPosition());
      }}
      onMouseLeave={() => enableTooltip && setShowTooltip(false)}
    >
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => {
          if (onChange) {
            onChange(e.target.value);
          }
        }}
        placeholder="Add"
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={cn(
          "w-full p-2 m-0 rounded border-none outline-none resize-none bg-transparent focus:bg-[#F3F4F6] font-sans font-normal text-sm",
          "placeholder:font-sans placeholder:font-normal placeholder:text-sm placeholder:leading-5 placeholder:text-[#0A0A0A]/60",
          textareaClassName
        )}
        style={{
          ...style,
          lineHeight: "1.25rem",
          height: enableScroll 
            ? `${(numberOfRows ?? 2) * 1.25}rem` 
            : `${(numberOfRows ?? 2) * 1 + 1}rem`,
          maxHeight: enableScroll 
            ? `${(numberOfRows ?? 2) * 1.25}rem` 
            : `${(numberOfRows ?? 2) * 1 + 1}rem`,
          overflowX: "hidden",  
          overflowY: enableScroll ? "auto" : "hidden",
          color: "#0A0A0A",
        }}
        rows={numberOfRows ?? 2}
      />
      {enableTooltip &&
        showTooltip &&
        !isFocused &&
        value &&
        tooltipStyle &&
        ReactDOM.createPortal(
          <div
            className="absolute z-50 left-0 top-full mt-1 bg-gray-700 rounded-md shadow-lg whitespace-pre-wrap break-words font-sans font-normal text-xs leading-[1rem]"
            style={{
              ...tooltipStyle,
              color: "#FAFAFA",
              width: "146px",
              maxWidth: "384px",
              paddingTop: "0.375rem",
              paddingRight: "0.75rem",
              paddingBottom: "0.375rem",
              paddingLeft: "0.75rem",
              gap: "8px",
              opacity: 1,
            }}
          >
            {value}
            <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-700 transform rotate-45" />
          </div>,
          document.body
        )}
    </div>
  );
};

export default TextCell;
